// Affiliates — the company's partner program dashboard.
//
// Add partners (they get an emailed portal login + tracked link), set each
// partner's commission %, record referred sales, and mark commissions paid.
// Paid invoices for a referred client post to the ledger automatically.
import { useEffect, useRef, useState } from "react";
import { Copy, Handshake, Plus, DollarSign, CheckCircle2 } from "lucide-react";
import { Shell } from "../components/Shell";
import { Modal } from "../components/Modal";
import { request } from "../lib/api";
import { useUser } from "../lib/auth";

interface Affiliate {
  id: string; name: string; email: string; code: string; link: string; legacy_link: string;
  slug: string | null; business_name: string | null; phone: string | null;
  commission_pct: number; destination_url: string | null; status: string;
  clicks: number | string; client_count: number; referral_count: number;
  commission_owed: number; commission_paid: number; sales_total: number;
}
interface AffiliateDetail {
  id: string; name: string; email: string; code: string; link: string; legacy_link: string;
  slug: string | null; business_name: string | null; phone: string | null;
  commission_pct: number; destination_url: string | null; status: string; clicks: number | string;
}
interface Referral {
  id: string; description: string; sale_amount: number; commission_amount: number;
  status: string; created_at: string; client_name: string | null;
}
interface Payout {
  id: string; amount: number; method: string | null; reference: string | null; paid_at: string; items: number;
}

const money = (n: number) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function AffiliatesPage() {
  const { user } = useUser();
  const canManage = user?.role === "owner" || user?.role === "manager";

  const [list, setList] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);

  // Detail dialog. `selected` is only WHICH partner is open; everything shown
  // comes from GET /api/affiliates/:id (+ the uncapped list aggregates) fetched
  // on open and after every mutation — never from a list-row snapshot. Money
  // controls are enabled only while detailState === "ready", nothing is in
  // flight, and no mutation is pending for this partner.
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const selectedId = useRef<string | null>(null);
  const [detail, setDetail] = useState<AffiliateDetail | null>(null);
  const [detailState, setDetailState] = useState<"loading" | "ready" | "failed">("loading");
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [ledger, setLedger] = useState<Referral[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  // Outstanding commission from the list endpoint's uncapped aggregate (the
  // ledger is capped at 500 rows, so it must not drive the payout amount).
  const [owed, setOwed] = useState(0);
  // Generation guard: every open/close bumps it; late responses for an older
  // generation (or another partner) are dropped instead of overwriting the dialog.
  const gen = useRef(0);
  // Fetch sequence: only the newest fetch for the current generation may apply.
  const fetchSeq = useRef(0);
  // Mutations pending per partner id — survives close/reopen, so a dialog
  // reopened mid-PATCH stays locked until a fetch started AFTER the commit lands.
  const [pending, setPending] = useState<Record<string, number>>({});
  const bumpPending = (id: string, d: number) => setPending((m) => ({ ...m, [id]: Math.max(0, (m[id] || 0) + d) }));

  // add form
  const [fName, setFName] = useState(""); const [fEmail, setFEmail] = useState("");
  const [fPct, setFPct] = useState("40"); const [fDest, setFDest] = useState("");
  const [fBiz, setFBiz] = useState(""); const [fPhone, setFPhone] = useState("");
  const [busy, setBusy] = useState(false);

  // record sale form
  const [saleDesc, setSaleDesc] = useState(""); const [saleAmt, setSaleAmt] = useState("");
  // edit pct
  const [editPct, setEditPct] = useState("");

  // List loads are sequenced too: a slow pre-commit response must not
  // overwrite the totals a post-commit response already put on the dashboard.
  const listSeq = useRef(0);
  async function load(): Promise<Affiliate[]> {
    const seq = ++listSeq.current;
    try {
      const r = await request<{ ok: true; affiliates: Affiliate[] }>("/api/affiliates");
      if (seq === listSeq.current) { setList(r.affiliates); setErr(null); }
      return r.affiliates;
    } catch (e: any) { if (seq === listSeq.current) setErr(e?.message || "Could not load affiliates."); throw e; }
    finally { setLoading(false); }
  }
  useEffect(() => { load().catch(() => {}); }, []);

  // Fetch the open partner's fresh row + ledger + payouts AND the list
  // aggregates, together. Locks the dialog until it succeeds; applies only if
  // the dialog is still on the same generation and no newer fetch started.
  async function fetchDetail(id: string, g: number): Promise<void> {
    const seq = ++fetchSeq.current;
    setDetailState("loading");
    try {
      const [r, rows] = await Promise.all([
        request<{ ok: true; affiliate: AffiliateDetail; referrals: Referral[]; payouts: Payout[] }>(`/api/affiliates/${id}`),
        load(),
      ]);
      if (g !== gen.current || seq !== fetchSeq.current) return;
      const row = rows.find((x) => x.id === id);
      setDetail(r.affiliate); setEditPct(String(r.affiliate.commission_pct));
      setLedger(r.referrals); setPayouts(r.payouts || []);
      setOwed(Number(row?.commission_owed ?? 0));
      setDetailErr(null); setDetailState("ready");
    } catch (e: any) {
      if (g !== gen.current || seq !== fetchSeq.current) return;
      setDetailState("failed");
      setDetailErr(e?.message || "Could not load this partner. Retry before recording anything.");
    }
  }

  function openDetail(a: Affiliate) {
    const g = ++gen.current;
    selectedId.current = a.id;
    setSelected({ id: a.id, name: a.name }); setDetail(null); setDetailErr(null); setOwed(0);
    setLedger([]); setPayouts([]); setSaleDesc(""); setSaleAmt(""); setEditPct(String(a.commission_pct));
    fetchDetail(a.id, g);
  }

  function closeDetail() { gen.current++; selectedId.current = null; setSelected(null); setDetail(null); setDetailErr(null); }

  // After a successful mutation: if the dialog is showing this partner (any
  // generation — it may have been closed and reopened meanwhile) refresh it
  // with a fetch that started after the commit; otherwise refresh the list.
  async function afterMutation(id: string) {
    if (selectedId.current === id) await fetchDetail(id, gen.current);
    else await load().catch(() => {});
  }

  const ready = detailState === "ready" && !!detail && !busy && !(selected && pending[selected.id]);

  async function addPartner() {
    setBusy(true); setAddErr(null);
    try {
      const r = await request<any>("/api/affiliates", {
        method: "POST",
        body: { name: fName.trim() || undefined, email: fEmail.trim(), commission_pct: Number(fPct), destination_url: fDest.trim() || undefined,
                business_name: fBiz.trim() || undefined, phone: fPhone.trim() || undefined },
      });
      setMsg(r.email_delivered
        ? `${r.affiliate.name} added — their login was emailed. They'll set their name and business on first sign-in.`
        : `${r.affiliate.name} added, but the welcome email could not be delivered${r.warning ? ` (${r.warning})` : ""}. They can use "Forgot password" on the partner sign-in page to get in.`);
      setAddOpen(false); setFName(""); setFEmail(""); setFPct("40"); setFDest(""); setFBiz(""); setFPhone("");
      load().catch(() => {});
    } catch (e: any) { setAddErr(e?.message || "Could not add the partner."); }
    finally { setBusy(false); }
  }

  async function recordSale() {
    if (!selected || !ready) return;
    const g = gen.current; const id = selected.id;
    setBusy(true); setDetailErr(null); bumpPending(id, 1);
    try {
      await request<any>(`/api/affiliates/${id}`, {
        method: "POST",
        body: { action: "record_sale", description: saleDesc.trim(), sale_amount: Number(saleAmt.replace(/[$,\s]/g, "")) },
      });
      if (g === gen.current) { setSaleDesc(""); setSaleAmt(""); }
      bumpPending(id, -1);
      await afterMutation(id);
    } catch (e: any) { bumpPending(id, -1); if (g === gen.current) setDetailErr(e?.message || "Could not record the sale."); }
    finally { setBusy(false); }
  }

  async function markPaid() {
    if (!selected || !ready) return;
    // Money moves and the partner sees a payout statement — ask before doing it.
    if (!window.confirm(
      `Mark all outstanding commissions PAID for ${selected.name}?\n\nThis writes a payout statement the partner will see in their portal. It cannot be undone from here.`
    )) return;
    const g = gen.current; const id = selected.id; const name = selected.name;
    setBusy(true); setDetailErr(null); bumpPending(id, 1);
    try {
      const r = await request<any>(`/api/affiliates/${id}`, { method: "POST", body: { action: "mark_paid", confirm: true } });
      setMsg(`Marked ${r.marked_paid} commission(s) paid for ${name}.`);
      bumpPending(id, -1);
      await afterMutation(id);
    } catch (e: any) { bumpPending(id, -1); if (g === gen.current) setDetailErr(e?.message || "Could not mark paid."); }
    finally { setBusy(false); }
  }

  async function savePct() {
    if (!selected || !ready) return;
    const g = gen.current; const id = selected.id; const name = selected.name; const pct = editPct;
    setBusy(true); setDetailErr(null); bumpPending(id, 1);
    try {
      await request<any>(`/api/affiliates/${id}`, { method: "PATCH", body: { commission_pct: Number(pct) } });
      setMsg(`Commission set to ${pct}% for ${name} (applies to future sales).`);
      bumpPending(id, -1);
      await afterMutation(id);
    } catch (e: any) { bumpPending(id, -1); if (g === gen.current) setDetailErr(e?.message || "Could not update."); }
    finally { setBusy(false); }
  }

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setMsg(`${label} copied: ${text}`);
  }

  return (
    <Shell title="Affiliates" subtitle="Your partner program — links, commissions, payouts" actions={
      canManage ? (
        <button className="primary-button" onClick={() => { setAddErr(null); setAddOpen(true); }}>
          <Plus size={14} aria-hidden="true" /> Add partner
        </button>
      ) : undefined
    }>
      {msg && <p className="muted small" style={{ marginBottom: 12 }}>{msg}</p>}
      {loading && <p className="muted small">Loading…</p>}
      {err && <p className="error-text">{err}</p>}

      {!loading && list.length === 0 && !err && (
        <div className="panel" style={{ textAlign: "center", padding: 40 }}>
          <Handshake size={34} aria-hidden="true" style={{ color: "var(--ne-chrome-2)", marginBottom: 10 }} />
          <h2 style={{ marginBottom: 6 }}>No partners yet</h2>
          <p className="muted small" style={{ maxWidth: 480, margin: "0 auto 16px" }}>
            Add an affiliate partner and they get their own tracked link and a portal login to watch
            their clicks, referrals, and commissions. You set each partner's percentage.
          </p>
          {canManage
            ? <button className="primary-button" onClick={() => { setAddErr(null); setAddOpen(true); }}>Add your first partner</button>
            : <p className="muted small">An owner or manager can add partners.</p>}
        </div>
      )}

      {list.length > 0 && (
        <div className="panel">
          <div className="table-list">
            {list.map((a) => (
              <div key={a.id} className="table-row table-row--wide" style={{ alignItems: "center", cursor: "pointer" }} onClick={() => openDetail(a)}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <strong>{a.business_name || a.name}</strong> <span className={`pill ${a.status === "active" ? "pill-green" : ""}`}>{a.status}</span>
                  <div className="muted small">{a.business_name ? `${a.name} · ` : ""}{a.email}</div>
                </div>
                <span style={{ minWidth: 60, fontWeight: 700 }}>{a.commission_pct}%</span>
                <span className="muted small" style={{ minWidth: 70 }}>{Number(a.clicks)} clicks</span>
                <span className="muted small" style={{ minWidth: 70 }}>{a.client_count ?? 0} clients</span>
                <span className="muted small" style={{ minWidth: 70 }}>{a.referral_count} sales</span>
                <span style={{ minWidth: 100, color: "var(--ne-warning-text)" }}>{money(a.commission_owed)} owed</span>
                <span className="muted small" style={{ minWidth: 90 }}>{money(a.commission_paid)} paid</span>
                <button className="ghost-button" onClick={(e) => { e.stopPropagation(); copy(a.link, "Link"); }}>
                  <Copy size={13} aria-hidden="true" /> Link
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* add partner */}
      <Modal open={addOpen} title="Add affiliate partner" onClose={() => setAddOpen(false)}
        footer={<>
          <button className="ghost-button" onClick={() => setAddOpen(false)}>Cancel</button>
          <button className="primary-button" disabled={busy || !fEmail.includes("@")} onClick={addPartner}>
            {busy ? "Adding…" : "Add & send login"}
          </button>
        </>}>
        <div className="stacked-form">
          <label className="form-field"><span>Email (their portal login) — the only thing you need</span>
            <input value={fEmail} onChange={(e) => setFEmail(e.target.value)} inputMode="email" placeholder="jordan@example.com" /></label>
          <details style={{ marginTop: 4 }}>
            <summary className="muted small" style={{ cursor: "pointer" }}>Already know their name and business? (optional)</summary>
            <div className="stacked-form" style={{ marginTop: 10 }}>
              <label className="form-field"><span>Name</span>
                <input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Jordan Smith" /></label>
              <div className="form-row-2">
                <label className="form-field"><span>Business name (goes in their link)</span>
                  <input value={fBiz} onChange={(e) => setFBiz(e.target.value)} placeholder="Smith Tax & Credit" /></label>
                <label className="form-field"><span>Phone</span>
                  <input value={fPhone} onChange={(e) => setFPhone(e.target.value)} inputMode="tel" /></label>
              </div>
              {fBiz.trim() && (
                <p className="muted small">Their branded link will be <code>{window.location.host}/with/{fBiz.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}</code></p>
              )}
            </div>
          </details>
          <div className="form-row-2">
            <label className="form-field"><span>Their share of what you collect (%)</span>
              <input value={fPct} onChange={(e) => setFPct(e.target.value)} inputMode="decimal" />
              <span className="muted small">40 = they get 40% of what you collect from each client they refer.</span></label>
            <label className="form-field"><span>Tracked-link destination (optional)</span>
              <input value={fDest} onChange={(e) => setFDest(e.target.value)} placeholder="https:// page their tracked /a/ link redirects to" />
              <span className="muted small">Only affects the short tracked link. Their branded /with/ link always opens your intake.</span></label>
          </div>
          <p className="muted small">
            They get an email with a portal login. On first sign-in they enter their own name and
            business name, which creates their branded referral link — then they see every client
            they refer, their progress, commissions, and payouts.
          </p>
          {addErr && <p className="error-text" role="alert">{addErr}</p>}
        </div>
      </Modal>

      {/* partner detail */}
      <Modal open={!!selected} title={selected ? `${selected.name}${detail ? ` — ${detail.commission_pct}%` : ""}` : ""} onClose={closeDetail} wide
        footer={<button className="ghost-button" onClick={closeDetail}>Close</button>}>
        {selected && (
          <>
            {detailState === "loading" && <p className="muted small">Loading partner…</p>}
            {detailErr && (
              <p className="error-text" role="alert" style={{ marginBottom: 10 }}>
                {detailErr}{detailState === "failed" && <> <button className="ghost-button" disabled={busy} onClick={() => fetchDetail(selected.id, gen.current)}>Retry</button></>}
              </p>
            )}
            {detail && (
              <>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                  <button className="ghost-button" onClick={() => copy(detail.link, "Branded link")}>
                    <Copy size={13} aria-hidden="true" /> Copy branded link
                  </button>
                  {detail.legacy_link && detail.legacy_link !== detail.link && (
                    <button className="ghost-button" onClick={() => copy(detail.legacy_link, "Tracked link")}>
                      <Copy size={13} aria-hidden="true" /> Copy tracked link{detail.destination_url ? " (redirects to their destination)" : ""}
                    </button>
                  )}
                  {canManage && (
                    <>
                      <label className="form-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                        <input style={{ width: 70 }} value={editPct} onChange={(e) => setEditPct(e.target.value)} inputMode="decimal" disabled={!ready} />
                        <button className="ghost-button" disabled={!ready} onClick={savePct}>Set %</button>
                      </label>
                      <button className="primary-button" disabled={!ready || owed <= 0} onClick={markPaid}>
                        <CheckCircle2 size={14} aria-hidden="true" /> Mark {money(owed)} paid
                      </button>
                    </>
                  )}
                </div>

                {canManage && (
                  <div className="sub-panel" style={{ marginBottom: 14 }}>
                    <strong style={{ display: "block", marginBottom: 8 }}><DollarSign size={14} aria-hidden="true" /> Record a referred sale</strong>
                    <div className="form-row-2">
                      <label className="form-field"><span>What was sold</span>
                        <input value={saleDesc} onChange={(e) => setSaleDesc(e.target.value)} placeholder="Invoice — Jane D." disabled={!ready} /></label>
                      <label className="form-field"><span>Amount the client paid you ($)</span>
                        <input value={saleAmt} onChange={(e) => setSaleAmt(e.target.value)} inputMode="decimal" placeholder="2500" disabled={!ready} />
                        <span className="muted small">What the referred client paid — their {detail.commission_pct}% comes off this. Invoices paid through the client portal post here automatically.</span></label>
                    </div>
                    <button className="primary-button" style={{ marginTop: 8 }} disabled={!ready || !saleDesc.trim() || !saleAmt.trim()} onClick={recordSale}>
                      Record — partner gets {detail.commission_pct}% of this
                    </button>
                  </div>
                )}

                <strong style={{ display: "block", marginBottom: 8 }}>Commission ledger</strong>
                {ledger.length === 0 && <p className="muted small">No referred sales recorded yet.</p>}
                {ledger.length > 0 && (
                  <div className="table-list">
                    {ledger.map((r) => (
                      <div key={r.id} className="table-row table-row--wide">
                        <span style={{ flex: "1 1 200px" }}>{r.description}{r.client_name ? <span className="muted small"> · {r.client_name}</span> : null}</span>
                        <span className="muted small" style={{ minWidth: 90 }}>{money(r.sale_amount)} sale</span>
                        <strong style={{ minWidth: 90 }}>{money(r.commission_amount)}</strong>
                        <span className={`pill ${r.status === "paid" ? "pill-green" : r.status === "void" ? "pill-red" : ""}`}>{r.status}</span>
                        <span className="muted small">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {payouts.length > 0 && (
                  <>
                    <strong style={{ display: "block", margin: "14px 0 8px" }}>Payout statements</strong>
                    <div className="table-list">
                      {payouts.map((p) => (
                        <div key={p.id} className="table-row table-row--wide">
                          <span style={{ flex: "1 1 200px" }}>{new Date(p.paid_at).toLocaleDateString()} · {p.items} item{p.items === 1 ? "" : "s"}</span>
                          <strong style={{ minWidth: 90 }}>{money(p.amount)}</strong>
                          <span className="muted small">{[p.method, p.reference].filter(Boolean).join(" · ") || "—"}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Modal>
    </Shell>
  );
}
